// CorpList.jsx
import React, { useRef, useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import { Input, Select, Pagination, SortHeader } from "@components";
import { SiteList, CorplistFormModal, AccountCreatedModal } from "@pages";

import { useCompanyList } from "@hooks";
import { AuthContext, useAdminCompany } from "@context";
import { ROLES } from "@constants/roles";
import { formatUseYn } from "@utils/formatUseYn";
import { toast } from 'react-toastify';

const CorpList = () => {
  const { auth, hasPermission } = useContext(AuthContext);
  const canWrite = hasPermission('COMPANY_WRITE');
  const canDelete = hasPermission('COMPANY_DELETE');
  const isAdmin = auth.role === ROLES.SUPER_ADMIN || auth.role === ROLES.ADMIN;
  const isSuperAdmin = auth.role === ROLES.SUPER_ADMIN;
  const { setAdminCompCode, setAdminCompName } = useAdminCompany();
  const siteListRef = useRef(null);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedCompCode, setSelectedCompCode] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [accountCreatedModal, setAccountCreatedModal] = useState({ isOpen: false, accountInfo: null });

  // 행 클릭 시: 전역 관리자 회사 선택값 갱신 + 하단 사업장 목록 조회 (스크롤 없음)
  const handleOpenSitesForCompany = (comp) => {
    if (isAdmin) {
      setAdminCompCode(comp.compCode || "");
      setAdminCompName(comp.compName || "");
    }
    const next = new URLSearchParams(searchParams);
    next.set("sitePageIndex", "1");
    setSearchParams(next, { replace: true });
  };
  const {
    methods,
    data,
    isLoading,
    isError,
    error,
    selectedItems,
    isAllChecked,
    onSubmit,
    handlePageChange,
    changePageSize,
    handleSelectAll,
    handleSelectItem,
    handleDelete,      // 삭제 로직 (이벤트X)
    corppageIndex,
    recordCountPerPage,
    refreshList,
  } = useCompanyList();

  const [searchParams, setSearchParams] = useSearchParams();


  // 등록 버튼
  const handleCreate = (e) => {
    e.preventDefault();
    setIsEditMode(false);
    setSelectedCompCode(null);
    setFormModalOpen(true);
  };

  // 수정 버튼
  const handleUpdate = (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      toast.warn("수정할 항목을 선택해주세요.");
      return;
    } else if (selectedItems.length > 1) {
      toast.warn("수정은 한 개 항목만 가능합니다.");
      return;
    }
    setIsEditMode(true);
    setSelectedCompCode(selectedItems[0]);
    setFormModalOpen(true);
  };

  // 더블클릭/하이퍼링크 클릭용 직접 수정 (compCode를 직접 받아 selectedItems 의존 없이 실행)
  const handleUpdateDirect = (compCode) => {
    setIsEditMode(true);
    setSelectedCompCode(compCode);
    setFormModalOpen(true);
  };

  // 모달 성공 콜백
  const handleFormModalSuccess = (response) => {
    refreshList();
    setFormModalOpen(false);
    // 등록 모드이고 계정 정보가 있으면 AccountCreatedModal 표시
    if (!isEditMode && response?.result?.account) {
      setAccountCreatedModal({ isOpen: true, accountInfo: response.result.account });
    } else {
      toast.success(response.message || "저장되었습니다.");
    }
  };

  // 삭제 버튼
  const handleDeleteCompanies = (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      toast.warn("삭제할 항목을 선택해주세요.");
      return;
    }
    if (window.confirm("선택한 사업장을 삭제하시겠습니까?")) {
      // handleDelete에는 이벤트X
      handleDelete();
    }
  };



  if (isLoading) {
    return <div>로딩 중...</div>;
  }
  if (isError) {
    console.error("isError:", error);
    return <div>오류가 발생했습니다: {error?.message || "알 수 없는 에러"}</div>;
  }

  return (
    <>
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="contents-wrap">
          {/* 페이지 타이틀 */}
          <div className="page-title-wrap">
            <h2>
              <span className="icon-dot-circle"></span>
              회사 목록
            </h2>
          </div>

          {/* 검색 영역 */}
          <div className="search-wrap">
            <div className="search-row">
              <div className="search-item">
                <Select
                  name="searchCnd"
                  options={[
                    { key: "compName", name: "회사명" },
                    { key: "bizNo", name: "사업자등록번호" },
                  ]}
                  defaultOption="검색 조건 선택"
                  className="input-select"
                />
                <Input
                  name="searchWrd"
                  placeholder="검색어를 입력하세요"
                  className="input-search"
                  style={{ width: "250px" }}
                />
                <button className="btn-search">조회</button>
              </div>
            </div>
          </div>

          {/* 목록 섹션 */}
          <section>
            <div className="section-tit-wrap">
              <h3 className="border-left-primary">
                &nbsp;회사 목록
                <span className="total-txt">
                  {" "} | 총 <span className="red">{data?.totalCount || 0}</span> 건
                </span>
              </h3>
              <div className="btn-right-box">
                <ul>
                  {/* 등록 버튼: 회사관리 > 회사 등록 탭에서 처리 */}
                  {canWrite && (
                  <li>
                    <button
                      type="button"
                      title="수정"
                      className="btn-update"
                      onClick={handleUpdate}
                    >
                      수정
                    </button>
                  </li>
                  )}
                  {canDelete && (
                  <li>
                    <button
                      type="button"
                      title="삭제"
                      className="btn-delete"
                      onClick={handleDeleteCompanies}
                    >
                      삭제
                    </button>
                  </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="table-container overflow-x-auto table-list-wrapper">
              <table className="table-list" style={{ width: "100%" }}>
                <colgroup>
                  <col style={{ width: "1%" }} />
                  <col style={{ width: "1%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "7%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "6%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={isAllChecked}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>번호</th>
                    <SortHeader field="COMP_CODE" label="회사코드" />
                    <SortHeader field="REGION" label="관할지역" />
                    <SortHeader field="BIZ_NO" label="사업자등록번호" style={{ textAlign: 'left' }} />
                    <SortHeader field="COMP_NAME" label="법인명" style={{ textAlign: 'left' }} />
                    <SortHeader field="REP_NAME" label="대표자명" style={{ textAlign: 'left' }} />
                    <th style={{ textAlign: 'left' }}>사업장 주소</th>
                    <SortHeader field="BIZ_TYPE" label="업태" />
                    <th>사내규모</th>
                    <th>담당자</th>
                    <th>사용여부</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.resultList && data.resultList.length > 0 ? (
                    data.resultList.map((item, idx) => (
                      <tr
                        key={item.compCode}
                        onClick={(e) => {
                          // e.detail === 1: 단순 클릭만 처리 (더블클릭 시 두 번째 클릭 무시)
                          if (e.detail === 1) {
                            handleSelectItem(item.compCode);
                            handleOpenSitesForCompany(item);
                          }
                        }}
                        onDoubleClick={() => handleUpdateDirect(item.compCode)}
                        style={{ cursor: "pointer" }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.compCode)}
                            onChange={() => handleSelectItem(item.compCode)}
                          />
                        </td>
                        <td>{(corppageIndex - 1) * recordCountPerPage + idx + 1}</td>
                        <td>{item.compCode}</td>
                        <td>{item.region}</td>
                        <td style={{ textAlign: 'left' }}>{item.bizNo}</td>
                        <td style={{ textAlign: 'left' }}>
                          <button
                            type="button"
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#007bff',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0,
                              font: 'inherit'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateDirect(item.compCode);
                            }}>
                            {item.compName}
                          </button>
                        </td>
                        <td style={{ textAlign: 'left' }}>{item.repName}</td>
                        <td style={{ textAlign: 'left' }}>
                          {item.addr1} {item.addr2 || ""}
                        </td>
                        <td>{item.bizType}</td>
                        <td>{item.sizeCategory}</td>
                        <td style={{ textAlign: 'left' }}>{item.managerNames || "-"}</td>
                        <td>{formatUseYn(item.useYn)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={13} className="table-empty" style={{ textAlign: "center" }}>
                        조회 결과가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
              <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#555' }}>페이지당 행 수</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={recordCountPerPage}
                onChange={(e) => changePageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {data?.paginationInfo && data.paginationInfo.totalRecordCount > 0 && (
              <Pagination
                totalPages={Math.ceil(
                  data.paginationInfo.totalRecordCount /
                    data.paginationInfo.recordCountPerPage
                )}
                currentPage={corppageIndex}
                onPageChange={handlePageChange}
                pageRangeDisplayed={10}
              />
            )}
          </section>
        </div>
      </form>


      
    </FormProvider>

    {/* 회사 등록/수정 모달 */}
    <CorplistFormModal
      isOpen={formModalOpen}
      onClose={() => setFormModalOpen(false)}
      isEdit={isEditMode}
      boardNum={selectedCompCode}
      onSuccess={handleFormModalSuccess}
    />

    {/* 계정 생성 완료 모달 */}
    <AccountCreatedModal
      isOpen={accountCreatedModal.isOpen}
      onClose={() => setAccountCreatedModal({ isOpen: false, accountInfo: null })}
      accountInfo={accountCreatedModal.accountInfo}
    />

    {/* 하단 사업장 목록 */}
    <div ref={siteListRef} style={{ marginTop: '24px' }}></div>
    <SiteList />
  </>
  );
};

export { CorpList };
